import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Eye, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ListPager, usePagedList } from './ListPager';
import { ListToolbar, useFilteredList } from './ListToolbar';

const EVENT_LABELS = {
  face_lost: { label: '顔ロスト（一時停止）', cls: 'bg-red-100 text-red-800' },
  face_found: { label: '顔検出（再開）', cls: 'bg-green-100 text-green-800' },
  session_start: { label: '受講開始', cls: 'bg-blue-100 text-blue-800' },
  session_end: { label: '受講終了', cls: 'bg-gray-100 text-gray-800' },
};

const fmtVideoTime = (sec) => {
  if (sec === undefined || sec === null || isNaN(sec)) return '-';
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

export default function MonitoringTab({ monitoringSummary, setMonitoringSummary, API, getAuthHeaders, formatDate }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const filter = useFilteredList(monitoringSummary, ['userName', 'userEmail', 'courseName', 'courseId']);
  const pager = usePagedList(filter.result);

  const refreshData = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/monitoring/summary`, { headers });
      setMonitoringSummary(res.data || []);
      toast.success(t('admin.common.updated'));
    } catch (e) {
      toast.error(t('admin.common.updateFailed'));
    }
  };

  const openDetails = async (item) => {
    setSelected(item);
    setOpen(true);
    setLoading(true);
    setLogs([]);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/monitoring/logs`, {
        headers,
        params: { user_id: item.userId, course_id: item.courseId },
      });
      setLogs(res.data || []);
    } catch (e) {
      toast.error(t('admin.common.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-600" />
          {t('admin.monitoring.title')}
        </h2>
        <Button size="sm" variant="outline" onClick={refreshData} data-testid="monitoring-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" />
          更新
        </Button>
      </div>

      <p className="text-sm text-gray-500">
        {t('admin.monitoring.description')}　行をクリックすると全ログを表示します。
      </p>

      <ListToolbar filter={filter} testId="monitoring" placeholder="受講者・メール・講習で検索" />
      {pager.total === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{t('admin.monitoring.noLogs')}</p>
          <p className="text-sm mt-1">{t('admin.monitoring.noLogsDescription')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="monitoring-table">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 text-sm font-medium text-gray-600">受講者</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">{t("admin.common.email")}</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">講習名</th>
                <th className="text-center p-3 text-sm font-medium text-gray-600">顔ロスト回数</th>
                <th className="text-left p-3 text-sm font-medium text-gray-600">最終記録</th>
              </tr>
            </thead>
            <tbody>
              {pager.paged.map((item, index) => (
                <tr
                  key={index}
                  className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                  data-testid={`monitoring-row-${index}`}
                  onClick={() => openDetails(item)}
                >
                  <td className="p-3 text-sm font-medium text-gray-900">{item.userName || '-'}</td>
                  <td className="p-3 text-sm text-gray-600">{item.userEmail || '-'}</td>
                  <td className="p-3 text-sm text-gray-600">{item.courseName || item.courseId}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.lostCount >= 10 ? 'bg-red-100 text-red-800' :
                      item.lostCount >= 5 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {item.lostCount}回
                    </span>
                  </td>
                  <td className="p-3 text-sm text-gray-500">{formatDate(item.lastEvent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListPager pager={pager} testId="monitoring" />
        </div>
      )}

      {/* Detail popup: all logs for selected user + course */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="monitoring-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              AI受講監視ログ 詳細
            </DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm bg-gray-50 rounded-lg p-3 mb-2">
              <div><span className="text-gray-500">受講者：</span><span className="font-medium">{selected.userName || '-'}</span></div>
              <div><span className="text-gray-500">メール：</span>{selected.userEmail || '-'}</div>
              <div><span className="text-gray-500">講習名：</span>{selected.courseName || selected.courseId}</div>
              <div><span className="text-gray-500">顔ロスト合計：</span><span className="font-medium text-red-700">{selected.lostCount}回</span></div>
              <div className="col-span-2"><span className="text-gray-500">最終記録：</span>{formatDate(selected.lastEvent)}</div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p>{t('admin.monitoring.noLogs')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 mb-2">全{logs.length}件</p>
              <table className="w-full text-sm" data-testid="monitoring-detail-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2 font-medium text-gray-600">イベント</th>
                    <th className="text-left p-2 font-medium text-gray-600">動画ID</th>
                    <th className="text-center p-2 font-medium text-gray-600">再生位置</th>
                    <th className="text-left p-2 font-medium text-gray-600">発生時刻</th>
                    <th className="text-left p-2 font-medium text-gray-600">記録時刻</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const ev = EVENT_LABELS[log.eventType] || { label: log.eventType, cls: 'bg-gray-100 text-gray-800' };
                    return (
                      <tr key={log.id || i} className="border-b" data-testid={`monitoring-detail-row-${i}`}>
                        <td className="p-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ev.cls}`}>
                            {ev.label}
                          </span>
                        </td>
                        <td className="p-2 text-gray-500 font-mono text-xs">{(log.videoId || '-').slice(0, 8)}</td>
                        <td className="p-2 text-center text-gray-700">{fmtVideoTime(log.details?.videoTime)}</td>
                        <td className="p-2 text-gray-600">{formatDate(log.timestamp)}</td>
                        <td className="p-2 text-gray-500">{formatDate(log.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
